import { useMemo, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  Plus,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Search,
  Star,
  Trash2,
  RotateCcw,
  X,
} from "lucide-react";
import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { AdminDrawer } from "@/components/admin/AdminDrawer";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { useSupplierCatalogProducts } from "@/features/supplierCatalog/hooks";
import {
  useActiveSupplierQuotation,
  useCreateSupplierQuotationItem,
  useDeleteSupplierQuotationItem,
  useSetActiveSupplierQuotation,
  useSupplierQuotation,
  useSupplierQuotationItems,
  useUpdateSupplierQuotation,
  useUpdateSupplierQuotationItem,
} from "@/features/supplierQuotation/hooks";
import type {
  SupplierQuotationItem,
  SupplierQuotationStatus,
  SupplierQuotationUnitType,
} from "@/features/supplierQuotation/types";
import {
  buildUnitPricePreview,
  getQuotationBasePriceSuffix,
  getQuotationBasePriceValue,
  getQuotationPriceInputLabel,
  isDirectKgUnit,
  PRICE_PER_UNIT_SUFFIX,
  QUOTATION_UNIT_LABELS,
  usesKgPerBox,
  usesUnitsPerBox,
} from "@/features/quotation/unitLabels";
import {
  formatBRL,
  formatDateBR,
  formatMoneyInput,
  parseMoneyInput,
} from "@/lib/timeAgo";
import { buildSupplierItemPayload } from "./itemPayload";

const CATEGORY_ORDER = [
  "LEGUMES",
  "FRUTAS",
  "VERDURAS",
  "GRAOS",
  "TEMPEROS",
  "OVOS",
  "OUTROS",
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  LEGUMES: "Legumes",
  FRUTAS: "Frutas",
  VERDURAS: "Verduras",
  GRAOS: "Grãos",
  TEMPEROS: "Temperos",
  OVOS: "Ovos",
  OUTROS: "Diversos",
};

const STATUS_LABELS: Record<SupplierQuotationStatus, string> = {
  not_started: "Não iniciada",
  in_progress: "Aberta",
  completed: "Concluída",
};

const STATUS_COLORS: Record<SupplierQuotationStatus, string> = {
  not_started: "var(--login-muted)",
  in_progress: "#d97706",
  completed: "#16a34a",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.625rem 0.75rem",
  background: "var(--login-bg)",
  border: "1px solid var(--login-border)",
  borderRadius: "0.5rem",
  color: "var(--login-text)",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "0.9375rem",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 600,
  fontSize: "0.75rem",
  letterSpacing: "0.06em",
  color: "var(--login-muted)",
};

const btnPrimary: React.CSSProperties = {
  flex: 1,
  padding: "0.75rem",
  background: "var(--login-accent)",
  border: "none",
  borderRadius: "0.5rem",
  color: "#fff",
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  fontSize: "1rem",
  letterSpacing: "0.06em",
  cursor: "pointer",
};

const btnDanger: React.CSSProperties = {
  flex: 1,
  padding: "0.75rem",
  border: "1px solid var(--login-error, #e05353)",
  borderRadius: "0.5rem",
  color: "var(--login-error, #e05353)",
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  fontSize: "1rem",
  letterSpacing: "0.06em",
  cursor: "pointer",
  background: "transparent",
};

type DrawerState = {
  productId: string;
  item?: SupplierQuotationItem;
};

function StatusBadge({ status }: { status: SupplierQuotationStatus }) {
  return (
    <span
      style={{
        fontFamily: "'Barlow Condensed', sans-serif",
        fontWeight: 700,
        textTransform: "uppercase",
        fontSize: "0.75rem",
        letterSpacing: "0.06em",
        color: STATUS_COLORS[status],
        background: `${STATUS_COLORS[status]}18`,
        padding: "0.15rem 0.5rem",
        borderRadius: "0.25rem",
        whiteSpace: "nowrap",
      }}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

export function SupplierListaDetailPage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { quotationId?: string };
  const quotationId = params.quotationId ?? "";
  const { data: quotation, isLoading: quotationLoading } =
    useSupplierQuotation(quotationId);
  const { data: activeQuotationSetting } = useActiveSupplierQuotation();
  const { data: itemsData, isLoading: itemsLoading } =
    useSupplierQuotationItems(quotationId);
  const { data: supplierProductsData, isLoading: productsLoading } =
    useSupplierCatalogProducts();

  const createItemMutation = useCreateSupplierQuotationItem(quotationId);
  const updateQuotationMutation = useUpdateSupplierQuotation();
  const updateItemMutation = useUpdateSupplierQuotationItem(quotationId);
  const deleteItemMutation = useDeleteSupplierQuotationItem(quotationId);
  const setActiveMutation = useSetActiveSupplierQuotation();

  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(CATEGORY_ORDER),
  );
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [pricePerBox, setPricePerBox] = useState("");
  const [pricePerKg, setPricePerKg] = useState("");
  const [unitType, setUnitType] = useState<SupplierQuotationUnitType>("kg");
  const [kgPerBox, setKgPerBox] = useState("");
  const [unitsPerBox, setUnitsPerBox] = useState("");

  function applyUnitType(nextUnitType: SupplierQuotationUnitType) {
    setUnitType(nextUnitType);
    if (!isDirectKgUnit(nextUnitType)) setPricePerKg("");
    if (!usesKgPerBox(nextUnitType)) setKgPerBox("");
    if (!usesUnitsPerBox(nextUnitType)) setUnitsPerBox("");
    if (isDirectKgUnit(nextUnitType)) setPricePerBox("");
  }

  const products = supplierProductsData?.items ?? [];
  const items = itemsData?.items ?? [];
  const itemByProductId = new Map(items.map((item) => [item.product_id, item]));

  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return products;
    return products.filter((product) =>
      product.name.toLowerCase().includes(query),
    );
  }, [products, searchQuery]);

  const grouped = filteredProducts.reduce<Record<string, typeof products>>(
    (acc, product) => {
      const category = product.category ?? "OUTROS";
      (acc[category] ??= []).push(product);
      return acc;
    },
    {},
  );

  const isPending =
    updateQuotationMutation.isPending ||
    setActiveMutation.isPending ||
    createItemMutation.isPending ||
    updateItemMutation.isPending ||
    deleteItemMutation.isPending;
  const isActiveQuotation = activeQuotationSetting?.value === quotationId;
  const pricePreview = useMemo(() => {
    return buildUnitPricePreview({
      unitType,
      pricePerBox: parseMoneyInput(pricePerBox),
      pricePerKg: parseMoneyInput(pricePerKg),
      kgPerBox: kgPerBox ? parseFloat(kgPerBox) : null,
      unitsPerBox: unitsPerBox ? parseFloat(unitsPerBox) : null,
    });
  }, [pricePerBox, pricePerKg, unitType, kgPerBox, unitsPerBox]);

  function toggleCategory(category: string) {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }

  function openDrawer(productId: string, _productName: string) {
    const item = itemByProductId.get(productId);
    setDrawer({
      productId,
      item,
    });
    setDrawerError(null);
    setPricePerBox(
      item?.price_per_box != null ? formatMoneyInput(item.price_per_box) : "",
    );
    setPricePerKg(
      item?.price_per_kg != null ? formatMoneyInput(item.price_per_kg) : "",
    );
    setUnitType(item?.unit_type ?? "kg");
    setKgPerBox(item?.kg_per_box ? String(item.kg_per_box) : "");
    setUnitsPerBox(item?.units_per_box ? String(item.units_per_box) : "");
  }

  function closeDrawer() {
    setDrawer(null);
    setDrawerError(null);
    setPricePerBox("");
    setPricePerKg("");
    setUnitType("kg");
    setKgPerBox("");
    setUnitsPerBox("");
  }

  async function handleSubmit() {
    if (!drawer) return;
    const result = buildSupplierItemPayload({
      unitType,
      pricePerBox: parseMoneyInput(pricePerBox),
      pricePerKg: parseMoneyInput(pricePerKg),
      unitsPerBox,
      kgPerBox,
    });
    if (result.error) {
      setDrawerError(result.error);
      return;
    }

    setDrawerError(null);
    try {
      if (drawer.item) {
        await updateItemMutation.mutateAsync({
          itemId: drawer.item.id,
          payload: result.payload,
        });
      } else {
        await createItemMutation.mutateAsync({
          product_id: drawer.productId,
          ...result.payload,
        });
      }
      closeDrawer();
    } catch (error) {
      setDrawerError(
        getUserFacingErrorMessage(
          error,
          "Não foi possível salvar a lista. Tente novamente.",
        ),
      );
    }
  }

  async function handleDelete() {
    if (!drawer?.item) return;
    setDrawerError(null);
    try {
      await deleteItemMutation.mutateAsync(drawer.item.id);
      closeDrawer();
    } catch (error) {
      setDrawerError(
        getUserFacingErrorMessage(
          error,
          "Não foi possível salvar a lista. Tente novamente.",
        ),
      );
    }
  }

  async function handleMarkDone() {
    if (!quotation) return;
    try {
      await updateQuotationMutation.mutateAsync({
        quotationId: quotation.id,
        payload: { status: "completed" },
      });
    } catch (error) {
      setDrawerError(
        getUserFacingErrorMessage(
          error,
          "Não foi possível salvar a lista. Tente novamente.",
        ),
      );
    }
  }

  async function handleReopen() {
    if (!quotation) return;
    try {
      await updateQuotationMutation.mutateAsync({
        quotationId: quotation.id,
        payload: { status: "in_progress" },
      });
    } catch (error) {
      setDrawerError(
        getUserFacingErrorMessage(
          error,
          "Não foi possível salvar a lista. Tente novamente.",
        ),
      );
    }
  }

  async function handleSetActive() {
    if (!quotation) return;
    try {
      await setActiveMutation.mutateAsync(quotation.id);
    } catch (error) {
      setDrawerError(
        getUserFacingErrorMessage(
          error,
          "Não foi possível salvar a lista. Tente novamente.",
        ),
      );
    }
  }

  const isLoading = quotationLoading || itemsLoading || productsLoading;

  return (
    <>
      <style>{`
        @keyframes fadeSlideIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        .cat-header { transition: filter 0.15s; }
        .cat-header:hover { filter: brightness(0.95); }
        .search-container {
          position: relative;
          width: 100%;
          margin-bottom: 1.25rem;
        }
        .search-input {
          width: 100%;
          padding: 0.75rem 2.5rem 0.75rem 2.5rem;
          background: var(--login-surface);
          border: 1px solid var(--login-border);
          border-radius: 0.625rem;
          color: var(--login-text);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9375rem;
          outline: none;
          box-sizing: border-box;
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .search-input:focus {
          border-color: var(--login-accent);
          background: var(--login-bg);
        }
        .search-input::placeholder {
          color: var(--login-muted);
          opacity: 0.55;
        }
        .search-clear-btn {
          position: absolute;
          right: 0.875rem;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: var(--login-muted);
          cursor: pointer;
          display: flex;
          align-items: center;
          padding: 0.125rem;
          transition: color 0.15s;
        }
        .search-clear-btn:hover { color: var(--login-text); }
        .btn-primary:disabled,
        .btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }
        .floating-stack {
          position: sticky;
          bottom: 5.5rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          z-index: 50;
          pointer-events: none;
          margin-top: 2rem;
        }
        .floating-pill {
          pointer-events: auto;
          display: flex;
          gap: 0.375rem;
          background: var(--login-surface);
          padding: 0.375rem;
          border-radius: 9999px;
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0,0,0,0.04);
          border: 1px solid var(--login-border);
          transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease;
          width: max-content;
          max-width: 92vw;
        }
        .floating-pill:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0,0,0,0.06);
        }
        .btn-pill {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.375rem;
          padding: 0.625rem 1.25rem;
          border-radius: 9999px;
          font-family: 'Barlow Condensed', sans-serif;
          font-weight: 700;
          font-size: 0.9375rem;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          cursor: pointer;
          white-space: nowrap;
          border: none;
          transition: all 0.2s ease;
        }
        .btn-pill:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-pill-success { background: #16a34a; color: white; }
        .btn-pill-success:hover:not(:disabled) { background: #15803d; }
        .btn-pill-accent { background: var(--login-accent); color: white; }
        .btn-pill-accent:hover:not(:disabled) { filter: brightness(0.9); }
        .btn-pill-outline { background: transparent; color: var(--login-text); border: 1px solid var(--login-border); }
        .btn-pill-outline:hover:not(:disabled) { background: var(--login-bg); }
      `}</style>

      <div
        style={{
          padding: "1.5rem",
          paddingBottom: "6rem",
          maxWidth: "800px",
          margin: "0 auto",
        }}
      >
        <div style={{ marginBottom: "1.25rem" }}>
          <AdminBreadcrumb
            items={[
              { label: "Lista", onClick: () => navigate({ to: "/lista" }) },
              {
                label: quotation
                  ? formatDateBR(quotation.date)
                  : "Carregando...",
              },
            ]}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "1.25rem",
            gap: "0.75rem",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              flexWrap: "wrap",
            }}
          >
            <h1
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 700,
                fontSize: "1.75rem",
                letterSpacing: "0.04em",
                color: "var(--login-text)",
                margin: 0,
              }}
            >
              {quotation ? formatDateBR(quotation.date) : "—"}
            </h1>
            {quotation ? <StatusBadge status={quotation.status} /> : null}
            {isActiveQuotation ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.35rem",
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--login-accent)",
                  background: "var(--login-accent)18",
                  padding: "0.15rem 0.5rem",
                  borderRadius: "0.25rem",
                }}
              >
                <Star
                  size={12}
                  fill="var(--login-accent)"
                  color="var(--login-accent)"
                />
                Ativa
              </span>
            ) : null}
          </div>
        </div>

        <div className="search-container">
          <div
            style={{
              position: "absolute",
              left: "0.875rem",
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--login-muted)",
              display: "flex",
              alignItems: "center",
              pointerEvents: "none",
            }}
          >
            <Search size={18} strokeWidth={2} />
          </div>
          <input
            type="text"
            className="search-input"
            placeholder="Buscar produto..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery ? (
            <button
              className="search-clear-btn"
              onClick={() => setSearchQuery("")}
              type="button"
            >
              <X size={16} strokeWidth={2} />
            </button>
          ) : null}
        </div>

        {isLoading ? (
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              color: "var(--login-muted)",
              fontSize: "0.875rem",
            }}
          >
            Carregando...
          </p>
        ) : null}

        {!isLoading && products.length === 0 ? (
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              color: "var(--login-muted)",
              fontSize: "0.875rem",
            }}
          >
            Nenhum produto disponível na sua lista.
          </p>
        ) : null}

        {!isLoading && products.length > 0 ? (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            {CATEGORY_ORDER.filter(
              (category) => (grouped[category]?.length ?? 0) > 0,
            ).map((category) => {
              const categoryProducts = grouped[category]!;
              const expanded = expandedCategories.has(category);
              const categoryPricedCount = categoryProducts.filter((product) =>
                itemByProductId.has(product.id),
              ).length;

              return (
                <div
                  key={category}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.375rem",
                  }}
                >
                  <button
                    className="cat-header"
                    onClick={() => toggleCategory(category)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "0.75rem 1rem",
                      paddingLeft: "0.875rem",
                      background: "var(--login-surface)",
                      border: "1px solid var(--login-border)",
                      borderLeft: "3px solid var(--login-accent)",
                      borderRadius: "0.5rem",
                      cursor: "pointer",
                      width: "100%",
                      textAlign: "left",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'Barlow Condensed', sans-serif",
                        fontWeight: 600,
                        fontSize: "0.8125rem",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: "var(--login-text)",
                      }}
                    >
                      {CATEGORY_LABELS[category] ?? category}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: "0.8125rem",
                          color: "var(--login-muted)",
                        }}
                      >
                        {categoryPricedCount}/{categoryProducts.length} com
                        preço
                      </span>
                      {expanded ? (
                        <ChevronDown size={15} color="var(--login-muted)" />
                      ) : (
                        <ChevronRight size={15} color="var(--login-muted)" />
                      )}
                    </div>
                  </button>

                  {expanded ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.375rem",
                      }}
                    >
                      {categoryProducts.map((product, index) => {
                        const item = itemByProductId.get(product.id);
                        return (
                          <div
                            key={product.id}
                            style={{
                              animation: "fadeSlideIn 0.18s ease both",
                              animationDelay: `${index * 0.04}s`,
                            }}
                          >
                            <button
                              onClick={() =>
                                openDrawer(product.id, product.name)
                              }
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                width: "100%",
                                padding: "0.875rem 1rem",
                                background: "var(--login-surface)",
                                border: `1px solid ${item ? "var(--login-border)" : "color-mix(in srgb, var(--login-accent) 55%, var(--login-border))"}`,
                                borderStyle: item ? "solid" : "dashed",
                                borderRadius: "0.5rem",
                                cursor: "pointer",
                                textAlign: "left",
                                gap: "0.75rem",
                                transition: "border-color 0.15s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor =
                                  "var(--login-accent)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = item
                                  ? "var(--login-border)"
                                  : "color-mix(in srgb, var(--login-accent) 55%, var(--login-border))";
                              }}
                            >
                              <div style={{ minWidth: 0 }}>
                                <p
                                  style={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontWeight: 500,
                                    fontSize: "0.9375rem",
                                    color: "var(--login-text)",
                                    margin: 0,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {product.name}
                                </p>
                                <p
                                  style={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: "0.8125rem",
                                    color: "var(--login-muted)",
                                    margin: "0.125rem 0 0",
                                  }}
                                >
                                  {item
                                    ? `${formatBRL(getQuotationBasePriceValue(item))} / ${getQuotationBasePriceSuffix(item.unit_type)}`
                                    : "Sem preço lançado hoje"}
                                </p>
                              </div>
                              <div
                                style={{
                                  textAlign: "right",
                                  flexShrink: 0,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-end",
                                  gap: "0.2rem",
                                }}
                              >
                                {item ? (
                                  <>
                                    <p
                                      style={{
                                        fontFamily:
                                          "'Barlow Condensed', sans-serif",
                                        fontWeight: 700,
                                        fontSize: "1rem",
                                        color: "var(--login-text)",
                                        margin: 0,
                                      }}
                                    >
                                      {formatBRL(item.price_per_unit)} /{" "}
                                      {PRICE_PER_UNIT_SUFFIX[item.unit_type]}
                                    </p>
                                    <span
                                      style={{
                                        fontFamily:
                                          "'Barlow Condensed', sans-serif",
                                        fontWeight: 700,
                                        fontSize: "0.78rem",
                                        letterSpacing: "0.05em",
                                        textTransform: "uppercase",
                                        color: "var(--login-accent)",
                                      }}
                                    >
                                      Editar
                                    </span>
                                  </>
                                ) : (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "0.25rem",
                                      padding: "0.5rem 0.75rem",
                                      minHeight: "44px",
                                      background: "var(--login-accent)",
                                      borderRadius: "0.375rem",
                                      color: "#fff",
                                      fontFamily:
                                        "'Barlow Condensed', sans-serif",
                                      fontWeight: 700,
                                      textTransform: "uppercase",
                                      fontSize: "0.8125rem",
                                      letterSpacing: "0.05em",
                                    }}
                                  >
                                    <Plus size={14} />
                                    Definir
                                  </span>
                                )}
                              </div>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        <AdminDrawer
          open={drawer !== null}
          onClose={closeDrawer}
          title={drawer?.item ? "Editar Preço" : "Adicionar Preço"}
        >
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.375rem",
              }}
            >
              <label style={labelStyle}>
                {getQuotationPriceInputLabel(unitType)}
              </label>
              <input
                style={inputStyle}
                type="text"
                inputMode="numeric"
                value={isDirectKgUnit(unitType) ? pricePerKg : pricePerBox}
                onChange={(e) =>
                  isDirectKgUnit(unitType)
                    ? setPricePerKg(formatMoneyInput(e.target.value))
                    : setPricePerBox(formatMoneyInput(e.target.value))
                }
                placeholder="0,00"
              />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.375rem",
              }}
            >
              <label style={labelStyle}>Unidade de venda</label>
              <div
                data-testid="supplier-unit-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: "0.375rem",
                }}
              >
                {(
                  Object.entries(QUOTATION_UNIT_LABELS) as Array<
                    [SupplierQuotationUnitType, string]
                  >
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      applyUnitType(value);
                    }}
                    style={{
                      minHeight: "42px",
                      padding: "0.5rem",
                      border: "1px solid var(--login-border)",
                      borderRadius: "0.5rem",
                      background:
                        unitType === value
                          ? "var(--login-accent)"
                          : "var(--login-surface)",
                      color: unitType === value ? "#fff" : "var(--login-muted)",
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      letterSpacing: "0.06em",
                      cursor: "pointer",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {usesUnitsPerBox(unitType) ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.375rem",
                }}
              >
                <label style={labelStyle}>Unidades por caixa</label>
                <input
                  value={unitsPerBox}
                  onChange={(e) => setUnitsPerBox(e.target.value)}
                  inputMode="decimal"
                  placeholder="0"
                  style={inputStyle}
                />
              </div>
            ) : null}

            {usesKgPerBox(unitType) ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.375rem",
                }}
              >
                <label style={labelStyle}>Kg por caixa</label>
                <input
                  style={inputStyle}
                  type="number"
                  step="0.1"
                  min="0"
                  value={kgPerBox}
                  onChange={(e) => setKgPerBox(e.target.value)}
                  placeholder="0"
                />
              </div>
            ) : null}

            {!isDirectKgUnit(unitType) && pricePreview ? (
              <div
                style={{
                  padding: "0.75rem",
                  background: "var(--login-surface)",
                  border: "1px solid var(--login-border)",
                  borderRadius: "0.5rem",
                }}
              >
                <p
                  style={{
                    ...labelStyle,
                    margin: "0 0 0.25rem 0",
                    color: "var(--login-accent)",
                  }}
                >
                  Preço por {pricePreview.label}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 700,
                    fontSize: "1.25rem",
                    color: "var(--login-text)",
                  }}
                >
                  {formatBRL(pricePreview.value)}
                </p>
              </div>
            ) : null}

            {drawerError ? (
              <div
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.875rem",
                  color: "var(--login-error, #e05353)",
                }}
              >
                {drawerError}
              </div>
            ) : null}

            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button
                type="button"
                className="btn-primary"
                style={btnPrimary}
                onClick={handleSubmit}
                disabled={isPending}
              >
                {drawer?.item ? "Salvar" : "Adicionar"}
              </button>
              {drawer?.item ? (
                <button
                  type="button"
                  className="btn-danger"
                  style={btnDanger}
                  onClick={handleDelete}
                  disabled={isPending}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.4rem",
                    }}
                  >
                    <Trash2 size={16} />
                    Remover
                  </span>
                </button>
              ) : null}
            </div>
          </div>
        </AdminDrawer>

        {quotation ? (
          <div
            className="floating-stack"
            style={{ zIndex: drawer !== null ? 0 : 40 }}
          >
            <div className="floating-pill">
              {quotation.status === "in_progress" ? (
                <button
                  className="btn-pill btn-pill-success"
                  onClick={handleMarkDone}
                  disabled={isPending}
                >
                  <CheckCircle size={16} strokeWidth={2.5} /> Concluir
                </button>
              ) : null}
              {quotation.status === "completed" ? (
                <>
                  <button
                    className="btn-pill btn-pill-outline"
                    onClick={handleReopen}
                    disabled={isPending}
                  >
                    <RotateCcw size={16} strokeWidth={2.5} /> Reabrir
                  </button>
                  <button
                    className={
                      isActiveQuotation
                        ? "btn-pill btn-pill-outline"
                        : "btn-pill btn-pill-accent"
                    }
                    onClick={isActiveQuotation ? undefined : handleSetActive}
                    disabled={isPending || isActiveQuotation}
                    style={
                      isActiveQuotation
                        ? {
                            background: "var(--login-accent)18",
                            color: "var(--login-accent)",
                            borderColor: "var(--login-accent)",
                            cursor: "default",
                            opacity: 1,
                          }
                        : undefined
                    }
                    aria-pressed={isActiveQuotation}
                  >
                    <Star
                      size={16}
                      strokeWidth={2.5}
                      fill={isActiveQuotation ? "currentColor" : "none"}
                    />{" "}
                    {isActiveQuotation ? "Ativada" : "Ativar"}
                  </button>
                </>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
